import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatPrice } from '../lib/currency';
import { CalendarRange } from 'lucide-react';

const dayLabel = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return { dow: d.toLocaleDateString('en-US', { weekday: 'short' }), dm: d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) };
};

// ±3-day cheapest-fare strip (live Data API indicative fares). Lets travellers shift the
// departure date to a cheaper day. Prices come straight from the API — never fabricated.
export const NearbyDatesStrip = ({ origin, destination, date, roundTrip, currency, onPick }) => {
  const [dates, setDates] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!origin || !destination || !date) return;
    setDates(null);
    api.nearbyDates({ origin, destination, date, round_trip: !!roundTrip, currency, window: 3 })
      .then(({ data }) => { if (alive) setDates(data.dates || []); })
      .catch(() => { if (alive) setDates([]); });
    return () => { alive = false; };
  }, [origin, destination, date, roundTrip, currency]);

  if (dates && dates.every((d) => d.price_inr == null)) return null; // nothing useful to show

  return (
    <div className="mb-4 rounded-2xl border border-border bg-card p-3" data-testid="nearby-dates-strip">
      <div className="flex items-center gap-2 mb-2 px-1">
        <CalendarRange className="h-4 w-4 text-accent" />
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cheapest fares on nearby dates</span>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
        {(dates || Array.from({ length: 7 })).map((d, i) => {
          if (!d) return <div key={i} className="h-16 w-24 shrink-0 rounded-xl bg-secondary/50 animate-pulse" />;
          const { dow, dm } = dayLabel(d.date);
          const active = d.is_center;
          const hasPrice = d.price_inr != null;
          return (
            <button
              key={d.date}
              onClick={() => !active && hasPrice && onPick?.(d.date)}
              disabled={active || !hasPrice}
              data-testid={`nearby-date-${d.date}`}
              className={`shrink-0 w-24 rounded-xl border px-2.5 py-2 text-left transition-colors ${active ? 'border-accent bg-accent/10 cursor-default' : hasPrice ? 'border-border hover:border-accent/50 hover:bg-secondary cursor-pointer' : 'border-dashed border-border opacity-60 cursor-not-allowed'} ${d.is_cheapest && !active ? 'ring-1 ring-emerald-500/40' : ''}`}
            >
              <p className="text-[11px] text-muted-foreground leading-tight">{dow}</p>
              <p className="text-xs font-semibold leading-tight">{dm}</p>
              <p className={`text-sm font-semibold tabular mt-0.5 ${d.is_cheapest ? 'text-emerald-600' : ''}`}>
                {hasPrice ? formatPrice(d.price_inr, currency) : '—'}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
